import { createFileRoute, Navigate, redirect } from '@tanstack/react-router';
import { CrmAccountCoveragePage, CrmPrimaryNavigation } from '@pages/crm';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import {
  crmCoverageSearchSchema,
  getCoverageProfilesDrilldown,
} from '@features/crm-account-coverage';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
  canAccessHelpdesk,
} from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/crm/analytics')({
  validateSearch: crmCoverageSearchSchema,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (!auth.isLoading && auth.isAuthenticated && !canAccessCrmCustomerDirectory(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: CrmAnalyticsRoute,
});

function CrmAnalyticsRoute() {
  const { locale } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const accessScope = user
    ? `molchanovs:${user.id}:${user.systemRole}:${JSON.stringify(permissions ?? [])}`
    : '';
  if (!canAccessCrmCustomerDirectory(ability))
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  return (
    <main className="min-w-0 p-6">
      <CrmPrimaryNavigation
        activeSection="analytics"
        canAccessProfiles
        canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
        canAccessHelpdesk={canAccessHelpdesk(ability)}
        locale={locale}
      />
      <CrmAccountCoveragePage
        key={accessScope}
        accessScope={accessScope}
        canReadSensitiveFields={ability.can('read', 'CrmCustomerProfileSensitive')}
        canReadTickets={ability.can('read', 'AnalyticsMove')}
        search={search}
        onSearchChange={(next) => {
          void navigate({ search: next });
        }}
        onViewProfilesWithoutAccounts={() => {
          void navigate({
            to: '/$locale/crm',
            params: { locale },
            search: getCoverageProfilesDrilldown(),
          });
        }}
      />
    </main>
  );
}
